import {useMemo} from "react";
import type {JSONContent} from "@tiptap/core";
import type {Printout} from "crqlar-react-common/dist/models/printout/printout.model";
import {
    findPrintoutVariable,
    getPrintoutVariableDisplayReference,
    normalizePrintoutDateFormat,
    normalizePrintoutVariableFallback,
} from "../../variables/printoutVariables";
import type {PrintoutVariableFormatting} from "../../variables/printoutVariables";
import {
    resolvePrintoutVariable,
    PrintoutVariableResolveContext,
    withPrintoutLanguageContext,
} from "../../variables/printoutVariableResolver";
import {parsePrintoutContent, resolvePrintoutDocument} from "../../core/printoutDocument";

export interface PrintPreviewVariableRow {
    label: string;
    reference: string;
    unresolved: boolean;
    value: string;
}

interface PrintoutVariableOccurrence extends PrintoutVariableFormatting {
    fallback?: unknown;
    reference: string;
}

const getPrintoutVariableReference = (attrs?: Record<string, unknown> | null) => {
    const key = typeof attrs?.key === "string" ? attrs.key : "";
    const language = typeof attrs?.language === "string" ? attrs.language : "";

    if (!key) return "";

    return language ? `${key}:${language}` : key;
};

// Row-bound variables are excluded because they require a list/table row context.
const collectPrintoutVariableNodes = (
    node: JSONContent,
    occurrences: Map<string, PrintoutVariableOccurrence>,
) => {
    if (node.type === "printoutVariable") {
        const reference = getPrintoutVariableReference(node.attrs);
        const dateFormat = normalizePrintoutDateFormat(node.attrs?.dateFormat);
        const occurrenceKey = [reference, dateFormat ?? ""].join("\u0000");

        if (reference && !occurrences.has(occurrenceKey)) {
            occurrences.set(occurrenceKey, {
                dateFormat,
                fallback: node.attrs?.fallback,
                reference,
            });
        }

        return;
    }

    node.content?.forEach(child => collectPrintoutVariableNodes(child, occurrences));
};

const resolvePrintPreviewVariableRow = (
    occurrence: PrintoutVariableOccurrence,
    context: PrintoutVariableResolveContext,
): PrintPreviewVariableRow => {
    const resolvedVariable = resolvePrintoutVariable(occurrence.reference, context, occurrence);
    const variable = resolvedVariable?.variable
        ?? findPrintoutVariable(occurrence.reference.split(":")[0]);
    const displayValue = resolvedVariable?.displayValue ?? "";
    const label = variable?.label ?? occurrence.reference;
    const reference = getPrintoutVariableDisplayReference(occurrence.reference);

    if (displayValue) {
        return {
            label,
            reference,
            unresolved: false,
            value: displayValue,
        };
    }

    return {
        label,
        reference,
        unresolved: true,
        value: variable
            ? normalizePrintoutVariableFallback(occurrence.fallback, variable)
            : "",
    };
};

export const usePrintoutDocumentVariables = (
    printout?: Printout,
    context?: PrintoutVariableResolveContext,
    language?: string,
): PrintPreviewVariableRow[] => useMemo(() => {
    if (!printout || !context) return [];

    const document = resolvePrintoutDocument(printout, language ?? context.language);
    if (!document) return [];

    const documentContext = withPrintoutLanguageContext(context, document.language);
    const occurrences = new Map<string, PrintoutVariableOccurrence>();
    const parsedContent = parsePrintoutContent(document.content);
    collectPrintoutVariableNodes(parsedContent, occurrences);

    return Array.from(occurrences.values())
        .map(occurrence => resolvePrintPreviewVariableRow(occurrence, documentContext));
}, [context, language, printout]);
